import _get from 'lodash/get'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Text as RNText, View, useWindowDimensions } from 'react-native'
import { tv } from 'tailwind-variants'

import { MAX_CONTENT_WIDTH } from '@flows-ui/constants'
import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import type { Text as TextProps } from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import { IMAGE } from '@ui-kit/assets/images/index.native'
import { HTMLRenderer } from '@ui-kit/components/HTMLRenderer'
import Icon from '@ui-kit/components/SvgIcon'
import * as typography from '@ui-kit/core/typography'
import { theme } from '@ui-kit/theme'

export const styles = tv({
  slots: {
    textContainer: 'flex flex-row gap-x-2',
  },
  variants: {
    align: {
      center: { textContainer: 'justify-center' },
      left: { textContainer: 'justify-start' },
      right: { textContainer: 'justify-end' },
    },
  },
  defaultVariants: { align: 'left' },
})

export const Text = (props: TextProps & { flowValues: FlowValues }) => {
  const windowDimensions = useWindowDimensions()
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })
  const { textContainer } = styles({ align: props.align })

  if (!props.text) {
    return null
  }

  const style = typography[props.style ?? 'body2']
  const translatedText = translateFunction(props.text)
  const emphasis = props.emphasis ?? 'medium'
  if (props.containsHTML) {
    const containerWidth =
      windowDimensions.width > MAX_CONTENT_WIDTH ? MAX_CONTENT_WIDTH : windowDimensions.width
    const contentWidth = containerWidth - theme.space.gutter * 2
    return <HTMLRenderer contentWidth={contentWidth} source={{ html: translatedText }} />
  }

  return (
    <View className={textContainer()}>
      {props.iconUrl ? <Icon imgSrc={_get(IMAGE.icons.small, props.iconUrl).default.src} /> : null}
      <RNText className={style({ emphasis, align: props.align, weight: props.weight })}>
        {translatedText}
      </RNText>
    </View>
  )
}
